import type { ErpModule } from '@/api/platform.api';
import { resolveModuleFromPath } from '@/config/appNavigation';
import { isLockedModulePath } from '@/utils/routeGuardUtils';
import { isPlatformAdminRoute } from '@/config/moduleDefaults';
import { getModuleEntryByErpModule } from '@/config/moduleEntries';
import { isGlobalWorkspaceRoute } from '@/constants/globalWorkspaceRoutes';
import { MODULE_HUB_PATH } from '@/constants/authRoutes';
import { isRouteAllowedForEntryModule } from '@/utils/moduleScope';

export const MODULE_ACCESS_DENIED_EVENT = 'fattah:module-access-denied';
export const PERMISSION_DENIED_EVENT = 'fattah:permission-denied';
export const PRE_SWITCH_PATH_KEY = 'fattah:preSwitchPath';

export type ModuleAccessDeniedDetail = {
  message: string;
  module?: ErpModule | null;
};

export type PermissionDeniedDetail = {
  message: string;
};

const PERMISSION_DENIED_HANDLED_KEY = 'fattah:permission-denied-handled';

export function markPermissionDeniedHandled(): void {
  sessionStorage.setItem(PERMISSION_DENIED_HANDLED_KEY, String(Date.now()));
}

export function consumePermissionDeniedHandled(): boolean {
  const raw = sessionStorage.getItem(PERMISSION_DENIED_HANDLED_KEY);
  if (!raw) return false;
  sessionStorage.removeItem(PERMISSION_DENIED_HANDLED_KEY);
  const age = Date.now() - Number(raw);
  return age >= 0 && age < 4000;
}

export function dispatchPermissionDenied(detail: PermissionDeniedDetail): void {
  markPermissionDeniedHandled();
  window.dispatchEvent(new CustomEvent(PERMISSION_DENIED_EVENT, { detail }));
}

/** URL still points at the previous app while workspace module has already switched. */
export function isStaleModulePath(pathname: string, workspaceModule: ErpModule | null | undefined): boolean {
  if (!workspaceModule) return false;
  if (isRouteAllowedForEntryModule(pathname, workspaceModule)) return false;
  const pathModule = resolveModuleFromPath(pathname);
  if (!pathModule || pathModule === workspaceModule) return false;
  return isRouteAllowedForEntryModule(pathname, pathModule);
}

export function workspaceModuleHome(module: ErpModule | null | undefined): string {
  if (!module) return MODULE_HUB_PATH;
  return getModuleEntryByErpModule(module)?.homePath ?? MODULE_HUB_PATH;
}

export function isModuleNotEnabledMessage(message: string | undefined | null): boolean {
  if (!message) return false;
  return /Module [A-Z_]+ is not enabled for this organization/i.test(message);
}

export function parseModuleFromAccessMessage(message: string): ErpModule | null {
  const match = message.match(/Module ([A-Z_]+) is not enabled/i);
  if (!match?.[1]) return null;
  return match[1] as ErpModule;
}

export function isOrgModuleSubscribed(
  module: ErpModule,
  enabledModules: string[],
  accessibleModules?: string[],
): boolean {
  if (accessibleModules != null && accessibleModules.length > 0) {
    return accessibleModules.includes(module);
  }
  return enabledModules.includes(module);
}

/** Whether the user may open this workspace URL (org subscription + launcher access). */
export function canAccessWorkspacePath(
  pathname: string,
  enabledModules: string[],
  launcherModuleCodes: string[],
  accessibleModules?: string[],
): boolean {
  const pathOnly = pathname.split('?')[0];
  if (!pathOnly || pathOnly === '/') return false;
  if (pathOnly.startsWith('/master')) return true;
  if (pathOnly.startsWith('/onboarding') || isLockedModulePath(pathOnly)) return true;
  if (isPlatformAdminRoute(pathOnly)) return true;
  if (isGlobalWorkspaceRoute(pathOnly)) return true;

  const pathModule = resolveModuleFromPath(pathOnly);
  if (!pathModule) {
    return pathOnly.startsWith('/org') || pathOnly.startsWith('/workspace');
  }
  if (!isOrgModuleSubscribed(pathModule, enabledModules, accessibleModules)) return false;
  return launcherModuleCodes.includes(pathModule);
}

export function rememberPreSwitchPath(pathname: string, search = ''): void {
  const full = `${pathname}${search}`;
  sessionStorage.setItem(PRE_SWITCH_PATH_KEY, full);
}

export function consumePreSwitchPath(): string | null {
  const value = sessionStorage.getItem(PRE_SWITCH_PATH_KEY);
  sessionStorage.removeItem(PRE_SWITCH_PATH_KEY);
  return value;
}

export function firstAccessibleModuleHome(
  launcherModuleCodes: string[],
): string | null {
  for (const code of launcherModuleCodes) {
    const home = getModuleEntryByErpModule(code)?.homePath;
    if (home) return home;
  }
  return null;
}

export function dispatchModuleAccessDenied(detail: ModuleAccessDeniedDetail): void {
  window.dispatchEvent(new CustomEvent(MODULE_ACCESS_DENIED_EVENT, { detail }));
}
